import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getPostBySlug } from "../data/posts";
import {
  RocketShape,
  StarField,
  Meteors,
  FloatingOrb,
} from "../Components/motion/space-primitives";
import Footer from "../Components/rebrand/Footer";

// Build a stable anchor id from a heading's text (shared by the TOC and headings).
const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Smooth-scroll to a section, accounting for the fixed navbar height.
const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top, behavior: "smooth" });
  }
};

// Sticky left-hand table of contents with scroll-spy highlighting.
const TableOfContents = ({ sections, accent }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-25% 0px -65% 0px" },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  if (sections.length === 0) return null;

  return (
    <nav className="sticky top-28">
      <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500">
        On this page
      </p>
      <ul className="m-0 list-none space-y-1.5 border-l border-white/10 p-0">
        {sections.map((s) => {
          const isActive = activeId === s.id;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => scrollToId(s.id)}
                className="-ml-px block w-full border-l-2 py-1.5 pl-4 text-left text-base font-medium transition-colors"
                style={
                  isActive
                    ? { color: accent, borderColor: accent }
                    : {
                        color: "rgba(156,163,175,1)",
                        borderColor: "transparent",
                      }
                }
              >
                {s.text}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

// Renders a single content block by its type. Accent drives themed headings.
const Block = ({ block, accent }) => {
  switch (block.type) {
    case "heading":
      return (
        <h2
          id={slugify(block.text)}
          className="scroll-mt-28 text-2xl md:text-3xl font-bold text-white mt-10 mb-4"
        >
          {block.text}
        </h2>
      );
    case "paragraph":
      return <p className="text-gray-300 leading-relaxed mb-5">{block.text}</p>;
    case "list":
      return (
        <ul className="list-disc list-outside pl-6 mb-6 space-y-2 marker:[color:var(--accent)]">
          {block.items.map((item, i) => (
            <li key={i} className="text-gray-300 leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <div
          className="border-l-4 rounded-r-lg p-4 mb-8"
          style={{
            borderColor: accent,
            backgroundColor: `${accent}14`,
          }}
        >
          <p className="text-gray-300 text-sm italic m-0">{block.text}</p>
        </div>
      );
    default:
      return null;
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const theme = post?.theme;
  const accent = theme?.accent || "#99DFAF";

  // Inject the article's brand font only while this page is mounted,
  // so the Canopy typeface stays scoped to these articles.
  useEffect(() => {
    if (!theme?.fontHref) return undefined;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = theme.fontHref;
    link.setAttribute("data-blog-font", "1");
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [theme?.fontHref]);

  // Sections feeding the table of contents: the severity block (if any) plus
  // every heading in the article body, in document order.
  const sections = useMemo(() => {
    const list = [];
    if (post?.severity)
      list.push({
        id: "findings-severity",
        text: "Findings severity breakdown",
      });
    (post?.content || []).forEach((block) => {
      if (block.type === "heading")
        list.push({ id: slugify(block.text), text: block.text });
    });
    return list;
  }, [post]);

  // Set the page title + meta description from the article; restore on unmount
  // (so returning to the landing/main brings back the default Nodefleet values).
  useEffect(() => {
    if (!post) return undefined;
    const prevTitle = document.title;
    const metaEl = document.querySelector('meta[name="description"]');
    const prevDesc = metaEl?.getAttribute("content");

    document.title = `${post.title} | Nodefleet`;
    metaEl?.setAttribute("content", post.excerpt);

    return () => {
      document.title = prevTitle;
      if (metaEl && prevDesc != null) metaEl.setAttribute("content", prevDesc);
    };
  }, [post]);

  if (!post) {
    return (
      <div className="relative -mt-24 min-h-screen bg-[#12111d] flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-4xl font-bold text-white mb-4">
          Article not found
        </h1>
        <p className="text-gray-400 mb-8">
          The article you are looking for does not exist or has been moved.
        </p>
        <Link
          to="/audit"
          className="bg-green-300 text-morado font-bold px-5 py-2 rounded-lg hover:bg-sky-300 transition-all no-underline"
        >
          Back
        </Link>
      </div>
    );
  }

  const buttonStyle = { backgroundColor: accent, color: "#06140A" };

  return (
    <div
      className="relative -mt-24 min-h-screen"
      style={{
        backgroundColor: theme?.bg || "#12111d",
        fontFamily: theme?.font,
        "--accent": accent,
      }}
    >
      {/* Hero */}
      <div className="relative h-[55vh] flex items-end overflow-hidden">
        {theme?.coverGradient ? (
          <div
            className="absolute inset-0"
            style={{ background: theme.coverGradient }}
          />
        ) : (
          <img
            src={post.cover}
            alt={post.title}
            className="absolute top-0 left-0 w-full h-full object-cover"
          />
        )}
        {/* Space backdrop: drifting starfield + meteors + theme-tinted orbs */}
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
          <div
            className="bg-drift absolute inset-y-0 left-0 flex h-full"
            style={{ width: "200%", animationDuration: "90s" }}
          >
            <div className="relative h-full w-1/2 shrink-0">
              <StarField count={60} />
            </div>
            <div className="relative h-full w-1/2 shrink-0">
              <StarField count={60} />
            </div>
          </div>
        </div>
        <Meteors count={3} className="z-0 opacity-70" />
        <FloatingOrb
          size={440}
          color={`${accent}30`}
          style={{ top: -130, right: "8%" }}
        />
        <FloatingOrb
          size={320}
          color={`${accent}24`}
          style={{ bottom: 20, left: "-6%" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, transparent 30%, ${
              theme?.bg || "#12111d"
            } 100%)`,
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 w-full px-8 pb-12 max-sm:px-6 lg:px-20"
        >
          <Link
            to="/audit"
            className="text-sm font-semibold no-underline hover:underline"
            style={{ color: accent }}
          >
            ← Back
          </Link>
          {theme?.logo && (
            <img
              src={theme.logo}
              alt="Canopy Network"
              className="h-6 w-auto mt-5 mb-3 opacity-90"
            />
          )}
          <div className="flex flex-wrap gap-2 mt-3 mb-4">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs font-semibold px-2 py-1 rounded-md"
                style={{ color: accent, backgroundColor: `${accent}1A` }}
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-400 mt-4">
            <span>{post.author}</span>
            <span>•</span>
            <span>{post.date}</span>
            <span>•</span>
            <span>{post.readingTime}</span>
          </div>
        </motion.div>
      </div>

      {/* Article body — TOC on the left, content on the right */}
      <div className="flex w-full gap-12 px-8 py-12 max-sm:px-6 lg:px-20">
        <aside className="hidden w-56 shrink-0 lg:block">
          <TableOfContents sections={sections} accent={accent} />
        </aside>
        <article className="min-w-0 flex-1">
          {/* Download CTA */}
          <div
            className="rounded-2xl p-6 mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border"
            style={{
              backgroundColor: theme?.surface || "rgba(42,39,75,0.5)",
              borderColor: theme?.border || `${accent}4D`,
            }}
          >
            <div>
              <h3 className="text-white font-bold text-lg mb-1">
                Full report (PDF)
              </h3>
              <p className="text-gray-400 text-sm m-0">
                Detailed findings, trust boundaries, and remediation roadmap.
              </p>
            </div>
            <a
              href={post.pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 font-bold px-6 py-3 rounded-lg transition-opacity hover:opacity-90 no-underline text-center"
              style={buttonStyle}
            >
              ⬇ Download PDF
            </a>
          </div>

          {/* Severity breakdown */}
          {post.severity && (
            <div className="relative mb-12 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
              {/* Decorative rocket flying along the top-right border */}
              <div className="pointer-events-none absolute -right-3 -top-4 rotate-45 opacity-80">
                <RocketShape uid="severity" flame width={52} height={92} />
              </div>
              <h2
                id="findings-severity"
                className="scroll-mt-28 text-2xl md:text-3xl font-bold text-white mb-6"
              >
                Findings severity breakdown
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {post.severity.map((s) => (
                  <div
                    key={s.level}
                    className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5"
                  >
                    {/* Top accent bar in the severity color */}
                    <span
                      className="absolute inset-x-0 top-0 h-1"
                      style={{ backgroundColor: s.color }}
                    />
                    <div
                      className="text-4xl font-bold"
                      style={{ color: s.color }}
                    >
                      {s.count}
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-white">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: s.color }}
                      />
                      {s.level}
                    </div>
                    <div className="mt-2 text-xs leading-relaxed text-gray-400">
                      {s.summary}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content blocks */}
          {post.content.map((block, i) => (
            <Block key={i} block={block} accent={accent} />
          ))}

          {/* Bottom download link */}
          <div
            className="mt-12 border-t pt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            style={{ borderColor: "rgba(255,255,255,0.1)" }}
          >
            <a
              href={post.pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="font-bold px-6 py-3 rounded-lg transition-opacity hover:opacity-90 no-underline"
              style={buttonStyle}
            >
              ⬇ Download the full PDF
            </a>
            <a
              href={post.pdfViewUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-semibold no-underline hover:underline"
              style={{ color: accent }}
            >
              View on GitHub →
            </a>
          </div>
        </article>
      </div>

      <Footer bg={theme?.bg} accent={accent} />
    </div>
  );
};

export default BlogPost;
